// 3-up COA cards with score bars and Recommended ribbon.
// criteria: [{ key, label, format?: 'bar'|'chip'|'value', invert?: boolean }]
// coa: { id, name, recommended, criteria: { [key]: { value, label?, tone? } } }

import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';

export function CoaComparisonCard({
  coa,
  criteria = [],
  isSelected,
  onSelect,
  recommendedLabel = 'RECOMMENDED',
}) {
  const tooltip = useOptionalRichTooltipController();
  const cls = `coa-card ${coa.recommended ? 'recommended' : ''} ${isSelected ? 'is-selected' : ''}`;
  return (
    <article
      className={cls}
      onClick={() => onSelect && onSelect(coa)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') onSelect && onSelect(coa);
      }}
    >
      {coa.recommended ? <span className="ribbon">{recommendedLabel}</span> : null}
      <header>
        <div className="eyebrow">{coa.scopeId || coa.scope || 'COA'}</div>
        <h3 className="coa-name">{coa.name || coa.title}</h3>
        {coa.summary ? <p className="coa-summary">{coa.summary}</p> : null}
      </header>
      <dl className="coa-criteria">
        {criteria.map((c) => {
          const cell = coa.criteria?.[c.key] || coa[c.key];
          const value = cell?.value ?? cell;
          const richTooltip = coa.criterionRationales?.[c.key] || cell?.richTooltip || null;
          const triggerProps =
            richTooltip && tooltip
              ? tooltip.getTriggerProps({
                  surfaceId: richTooltip.surfaceId,
                  payload: richTooltip,
                  testIdPrefix: 'rich-tooltip',
                  ariaLabel: `${coa.name || coa.id} ${c.label}`,
                  onClick: (event) => {
                    event.stopPropagation();
                    onSelect && onSelect(coa);
                  },
                  onKeyDown: (event) => {
                    event.stopPropagation();
                    if (event.key === 'Enter' || event.key === ' ') onSelect && onSelect(coa);
                  },
                })
              : {};
          const CriterionTag = richTooltip ? 'button' : 'div';
          if (c.format === 'chip') {
            return (
              <CriterionTag
                key={c.key}
                type={richTooltip ? 'button' : undefined}
                className={`coa-criterion${richTooltip ? ' coa-criterion-trigger' : ''}`}
                data-testid={`coa-criterion-${coa.id}-${c.key}`}
                {...triggerProps}
              >
                <dt>{c.label}</dt>
                <dd>
                  <span className={`chip ${cell?.tone || 'slate'} sm`}>
                    {cell?.label || value || '—'}
                  </span>
                </dd>
              </CriterionTag>
            );
          }
          if (c.format === 'bar') {
            const pct = Math.max(0, Math.min(100, Number(value) || 0));
            return (
              <CriterionTag
                key={c.key}
                type={richTooltip ? 'button' : undefined}
                className={`coa-criterion${richTooltip ? ' coa-criterion-trigger' : ''}`}
                data-testid={`coa-criterion-${coa.id}-${c.key}`}
                {...triggerProps}
              >
                <dt>{c.label}</dt>
                <dd>
                  <div className="score-bar">
                    <i className={cell?.tone || ''} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="mono small">{pct}</span>
                </dd>
              </CriterionTag>
            );
          }
          return (
            <CriterionTag
              key={c.key}
              type={richTooltip ? 'button' : undefined}
              className={`coa-criterion${richTooltip ? ' coa-criterion-trigger' : ''}`}
              data-testid={`coa-criterion-${coa.id}-${c.key}`}
              {...triggerProps}
            >
              <dt>{c.label}</dt>
              <dd className="mono">{value ?? '—'}</dd>
            </CriterionTag>
          );
        })}
      </dl>
    </article>
  );
}
